import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import type {
  MarkPaidResponse,
  MonthlyBillInput,
  MonthlyBillMutateResponse,
  MonthlyBillsResponse,
} from './contractsMirror.js';
import type { MonthlyBillsSearch } from './searchParams.js';

/**
 * TanStack Query hooks for the COARSE monthly-bills endpoint (the strangler-fig + coarse-endpoint
 * proof, CARVE-07). One query key per `{month}` — changing the month filter triggers exactly one
 * refetch of `GET /api/v1/monthly-bills?month=…`. The SERVER computes the enriched bills (next-due +
 * per-month paid badge) + the scheduled-this-month sum (22-12); the SPA only renders the returned
 * response (no client re-aggregation, no new N+1 — threat T-22-tenant / CARVE-07). The list GET is a
 * PURE READ.
 *
 * Every request goes through `apiFetch` (the only fetch path — `credentials:'include'` +
 * `X-Tenant-Subdomain` baked in, single-flight 401 refresh). The tenant is derived server-side from
 * the auth context; the SPA NEVER sends a tenant id param (threat T-22-tenant).
 */

/** Build the stable query key for a filter (one cache entry per month). */
export function monthlyBillsQueryKey(search: MonthlyBillsSearch): readonly unknown[] {
  return ['monthly-bills', search.month ?? null] as const;
}

/** Build the coarse-endpoint request path with the active month filter (no tenant param). */
export function monthlyBillsRequestPath(search: MonthlyBillsSearch): string {
  const params = new URLSearchParams();
  if (search.month) params.set('month', search.month);
  const qs = params.toString();
  return qs ? `/api/v1/monthly-bills?${qs}` : '/api/v1/monthly-bills';
}

export function useMonthlyBillsQuery(search: MonthlyBillsSearch) {
  return useQuery<MonthlyBillsResponse>({
    queryKey: monthlyBillsQueryKey(search),
    queryFn: () => apiFetchJson<MonthlyBillsResponse>(monthlyBillsRequestPath(search)),
  });
}

/** The bill-create mutation. NO tenantId — the server resolves the tenant from the principal. */
export function useCreateBillMutation() {
  const queryClient = useQueryClient();
  return useMutation<MonthlyBillMutateResponse, Error, MonthlyBillInput>({
    mutationFn: (input) =>
      apiFetchJson<MonthlyBillMutateResponse>('/api/v1/monthly-bills', {
        method: 'POST',
        body: JSON.stringify(input),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['monthly-bills'] });
    },
  });
}

/** The bill-edit mutation. PATCHes `/api/v1/monthly-bills/:id`; the financials.edit gate holds. */
export function useUpdateBillMutation() {
  const queryClient = useQueryClient();
  return useMutation<MonthlyBillMutateResponse, Error, { billId: number; input: MonthlyBillInput }>(
    {
      mutationFn: ({ billId, input }) =>
        apiFetchJson<MonthlyBillMutateResponse>(`/api/v1/monthly-bills/${billId}`, {
          method: 'PATCH',
          body: JSON.stringify(input),
          headers: { 'Content-Type': 'application/json' },
        }),
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: ['monthly-bills'] });
      },
    },
  );
}

/** Archive a bill (22-25). POSTs `/api/v1/monthly-bills/:id/archive`; financials.edit gate holds. */
export function useArchiveBillMutation() {
  const queryClient = useQueryClient();
  return useMutation<MonthlyBillMutateResponse, Error, { billId: number }>({
    mutationFn: ({ billId }) =>
      apiFetchJson<MonthlyBillMutateResponse>(`/api/v1/monthly-bills/${billId}/archive`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['monthly-bills'] });
    },
  });
}

/** Restore an archived bill (22-25). POSTs `/api/v1/monthly-bills/:id/restore`. */
export function useRestoreBillMutation() {
  const queryClient = useQueryClient();
  return useMutation<MonthlyBillMutateResponse, Error, { billId: number }>({
    mutationFn: ({ billId }) =>
      apiFetchJson<MonthlyBillMutateResponse>(`/api/v1/monthly-bills/${billId}/restore`, {
        method: 'POST',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['monthly-bills'] });
    },
  });
}

/**
 * The inline mark-as-paid mutation (UI-SPEC "mark-as-paid"). PATCHes
 * `/api/v1/monthly-bills/:id/mark-paid` with the billing month; the server re-checks the
 * financials.edit permission (T-22-privesc) + scopes the write to the principal tenant (SEC-11). On
 * success the list is invalidated so the row's paid badge re-syncs. NO tenantId in the body.
 */
export function useMarkPaidMutation() {
  const queryClient = useQueryClient();
  return useMutation<MarkPaidResponse, Error, { billId: number; month: string }>({
    mutationFn: ({ billId, month }) =>
      apiFetchJson<MarkPaidResponse>(`/api/v1/monthly-bills/${billId}/mark-paid`, {
        method: 'PATCH',
        body: JSON.stringify({ month }),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['monthly-bills'] });
    },
  });
}
